import React, { useState, useRef } from "react";
import { Upload, Modal, message, Slider, Row, Col } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import Pica from "pica";

const ImageUploader = ({
  key,
  width,
  height,
  title,
  recommendedSize,
  onChange,
  defaultFileList,
  fieldName,
}) => {
  const [fileList, setFileList] = useState(
    defaultFileList ? defaultFileList : []
  );
  const [previewOpen, setPreviewOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [zoom, setZoom] = useState(1); // Default zoom level
  const cropperRef = useRef(null);
  const pica = Pica();

  const handleUpload = async ({ file, onError, onSuccess }) => {
    const reader = new FileReader();

    reader.onload = () => {
      const img = new Image();
      img.src = reader.result;

      img.onload = () => {
        // if (img.width < width || img.height < height) {
        //   message.error(
        //     `Image dimensions must be at least ${width}px by ${height}px.`
        //   );
        //   onError(new Error("Image dimensions are too small."));
        //   return;
        // }

        setImageSrc(reader.result); // Set the image source for preview
        setPreviewOpen(true); // Open the cropper modal
        onSuccess("File is valid and ready for cropping.");
      };

      img.onerror = () => {
        message.error("Failed to load image. Please try again.");
        onError(new Error("Image loading error."));
      };
    };

    reader.onerror = () => {
      message.error("Error reading file.");
      onError(new Error("File reading error."));
    };

    reader.readAsDataURL(file); // Start reading the file as a data URL
  };

  // const handleCrop = async () => {
  //   const cropper = cropperRef.current?.cropper;
  //   if (!cropper) return;

  //   const croppedCanvas = cropper.getCroppedCanvas({
  //     width: width,
  //     height: height,
  //     imageSmoothingEnabled: true,
  //     imageSmoothingQuality: "high",
  //   });

  //   try {
  //     const compressedCanvas = await pica.resize(croppedCanvas, croppedCanvas, {
  //       quality: 0.8,
  //     });

  //     compressedCanvas.toBlob((blob) => {
  //       const newFile = new File([blob], "cropped-image.png", {
  //         type: "image/png",
  //       });

  //       const updatedFileList = [
  //         {
  //           uid: "-1",
  //           name: newFile.name,
  //           status: "done",
  //           originFileObj: newFile,
  //           url: URL.createObjectURL(newFile),
  //         },
  //       ];

  //       setFileList(updatedFileList);

  //       if (onChange) {
  //         onChange(updatedFileList);
  //       }

  //       setPreviewOpen(false);
  //       setImageSrc(null);
  //     }, "image/png");
  //   } catch (error) {
  //     message.error("An error occurred during image processing.");
  //   }
  // };

  const handleCrop = async () => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;
  
    const croppedCanvas = cropper.getCroppedCanvas({
      width: width,
      height: height,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: "high",
    });
  
    try {
      const picaInstance = new Pica();
      let quality = 0.8;
      let blob;
      const maxSizeKB = 400;
  
      do {
        const compressedCanvas = await picaInstance.resize(croppedCanvas, croppedCanvas, {
          quality: quality,
        });
  
        blob = await new Promise((resolve) =>
          compressedCanvas.toBlob(resolve, "image/jpeg", quality)
        );
  
        quality -= 0.05; // Reduce quality if file size exceeds limit
      } while (blob.size / 1024 > maxSizeKB && quality > 0.1);
  
      const newFile = new File([blob], "cropped-image.jpg", {
        type: "image/jpeg",
      });
  
      const updatedFileList = [
        {
          uid: "-1",
          name: newFile.name,
          status: "done",
          originFileObj: newFile,
          url: URL.createObjectURL(newFile),
        },
      ];
  
      setFileList(updatedFileList);
  
      if (onChange) {
        onChange(updatedFileList);
      }
  
      setPreviewOpen(false);
      setImageSrc(null);
    } catch (error) {
      message.error("An error occurred during image processing.");
    }
  };
  

  const handleZoomChange = (value) => {
    setZoom(value);
    const cropper = cropperRef.current?.cropper;
    if (cropper) {
      cropper.zoomTo(value);
    }
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div className="ant-upload-text">Upload</div>
    </div>
  );

  const handleRemoveImage = (file) => {
    setFileList((prevList) => prevList.filter((item) => item.uid !== file.uid));
  };

  return (
    <>
      <div key={key}></div>
      <Upload
        accept="image/*"
        customRequest={handleUpload}
        fileList={fileList}
        listType="picture-card"
        onRemove={handleRemoveImage}
      >
        {uploadButton}
      </Upload>
      <h5 className="font-weight-normal">
        {recommendedSize && `Recommended size: ${recommendedSize}`}
      </h5>

      <Modal
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        onOk={handleCrop}
        okText="Crop and Compress"
        bodyStyle={{ padding: 0 }}
        style={{ top: 20, width: "90%", maxWidth: "900px" }}
        width="90%"
        title="Crop Image"
      >
        {imageSrc && (
          <div>
            <Row justify="center" align="middle" style={{ marginBottom: 16 }}>
              <Col span={2}>
                <div style={{ textAlign: "left", fontWeight: "bold" }}>Zoom</div>
              </Col>
              <Col span={12}>
                <Slider
                  min={0.1}
                  max={2}
                  step={0.01}
                  value={zoom}
                  onChange={handleZoomChange}
                  style={{ marginBottom: 16, width: "100%", marginTop: 16 }}
                />
              </Col>
            </Row>
            <Cropper
              src={imageSrc}
              style={{ height: 400, width: "100%" }}
              aspectRatio={width / height}
              guides={false}
              dragMode="move"
              ref={cropperRef}
            />
          </div>
        )}
      </Modal>
    </>
  );
};

export default ImageUploader;
